import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="argyle"
export default class extends Controller {
  static values = {
    charityCareApplicationId: Number,
    billId: Number,
    linkToken: String,
    linkKey: String,
    environment: String,
    apiHost: String
  }

  openPaystubs(e) {
    e.preventDefault()
    this.open()
  }

  open() {
    const payload = {
      resource_type: 'CharityCareApplication',
      resource_id: this.charityCareApplicationIdValue,
      bill_id: this.billIdValue
    }

    // Prefer server-side created Link token when available; otherwise request it and fallback to Link Key
    if (this.hasLinkTokenValue && this.linkTokenValue) {
      this.launchLink({ link_token: this.linkTokenValue }, payload)
      return
    }

    // Request a link token from backend
    fetch('/api/v1/argyle/create_link_token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(r => r.json())
      .then(data => {
        if (data.error) { throw new Error(data.error) }
        if (data.argyle_user_token) {
          this.launchLink({ user_token: data.argyle_user_token }, payload)
        } else if (data.link_key || (this.hasLinkKeyValue && this.linkKeyValue)) {
          const link_key = data.link_key || this.linkKeyValue
          this.launchLink({ link_key }, payload)
        } else {
          throw new Error('missing_link_credentials')
        }
      })
      .catch(err => {
        // If API fails (e.g., user not logged in), fall back to link_key if available
        if (this.hasLinkKeyValue && this.linkKeyValue) {
          this.launchLink({ link_key: this.linkKeyValue }, payload)
        } else {
          if (window.appsignal) { appsignal.sendError(err) } else { console.error(err) }
        }
      })
  }

  launchLink(config, payload) {
    const self = this
    // Capture these values to ensure callback closure has correct references
    const expectedResourceId = payload.resource_id
    const expectedResourceType = payload.resource_type
    
    const onAccountConnected = (connection) => {
      console.log('Argyle connection received:', {
        connection,
        expectedResourceId,
        expectedResourceType
      })
      
      // ensure we're sending to the correct resource
      if (!connection || !connection.accountId || !connection.userId) {
        console.error('Invalid Argyle connection object:', connection)
        if (window.appsignal) {
          appsignal.sendError(new Error('Invalid Argyle connection: missing accountId or userId'))
        }
        return
      }

      // Flip the express UI to "Connected — we're pulling your data" immediately, the instant the
      // account links (i.e. as the modal closes), rather than waiting on the /connected round-trip
      // below (which persists the account + enqueues the download and takes a few seconds). The
      // server broadcast supersedes this once data lands.
      self.dispatch('connected', { bubbles: true })

      // Notify backend to fetch paystubs
      fetch('/api/v1/argyle/connected', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          connection: connection,
          resource_type: expectedResourceType,
          resource_id: expectedResourceId,
          bill_id: payload.bill_id
        })
      }).then(response => {
        if (!response.ok) {
          return response.json().then(data => {
            throw new Error(data.error || 'Connection failed')
          })
        }
        return response.json()
      }).then(data => {
        console.log('Argyle connection successful:', data)
        // (The "connecting" UI was already revealed above, the moment the account linked.)
      }).catch(err => {
        console.error('Argyle connection error:', err)
        if (window.appsignal) { 
          appsignal.sendError(err) 
        }
        alert('There was an error connecting your paystubs. Please try again or contact support.')
      })
    }

    const argyle = window.Argyle && window.Argyle.create({
      ...config,
      linkKey: config.link_key,
      userToken: config.user_token,
      sandbox: this.hasEnvironmentValue ? (this.environmentValue === 'sandbox') : false,
      onAccountConnected,
      onExit: () => { },
      onError: (error) => {
        console.error('Argyle SDK error:', error)
        if (window.appsignal) {
          appsignal.sendError(new Error('Argyle SDK error: ' + JSON.stringify(error)))
        }
      }
    })

    if (argyle && argyle.open) { argyle.open() }
  }
}
